// Manual sections 14–16: handing calls to reviewers, working a call end to end,
// and finding calls again afterwards. See ../index.js for the schema.

const reviewing = [
  {
    id: 'assigning',
    group: 'Review',
    title: 'Assigning calls for review',
    lede: 'Campaign Home is where an Admin hands processed calls to reviewers; each role then finds its calls on its own page.',
    roles: ['sAdm', 'adm', 'qa', 'callAgent'],
    blocks: [
      { type: 'h3', text: 'Campaign Home (Admins and Super Admins)' },
      {
        type: 'p',
        text:
          '**Campaign Home** is the landing page for Admins and Super Admins: the calls across your campaigns, with headline figures and a results chart above the call table. It loads recent calls first; `Load More Calls` below the table (it names the campaign once you pick one) brings in older ones. The figures and chart cover the calls loaded so far.',
      },
      {
        type: 'steps',
        items: [
          {
            title: 'Narrow the list.',
            text:
              'Start with `Select Campaign`, then set `Filter by Date` (click `Apply`), `Compliance Priority` and `Assigned To`. Once you pick a specific campaign rather than All, `Select Template` (the scorecard) and `Select Job (Upload Batch)` appear too.',
          },
          {
            title: 'Use the chart and tiles as filters.',
            text:
              'Click any bar in `Call Results Distribution` to filter the table to pass, medium or fail calls. The `Compliance Failed` tile filters to compliance failures the same way. Under **Agent Performance** you can also pick agents, a rating and `Assessed` or `Not Assessed`.',
          },
          {
            title: 'Select the calls.',
            text: 'Tick the boxes on the left, or the `All` box in the header to select every call that matches your filters.',
          },
          {
            title: 'Assign them.',
            text:
              'Open `Assign` above the table. `Assign to me` gives the selected calls to you. `Choose QA agent…` opens a list of reviewers: tick one or more and click `Attach`. Calls are shared out in turn between the reviewers you tick.',
          },
        ],
      },
      {
        type: 'ul',
        items: [
          'Only calls that have finished processing can be assigned. If none of the selected calls have, you are told so and nothing changes.',
          'The **Assignee** column shows who has each call, or *Unassigned*. To see your own, pick yourself (shown as **Me**) under `Assigned To`.',
          'Click `View` in a row to open that call.',
        ],
      },
      { type: 'h3', text: 'Assigned to me (QA Agents)' },
      {
        type: 'p',
        text:
          'A QA Agent’s calls are on **Assigned to me**. Filter by campaign, and by `All Calls`, `Assessed` or `Not Assessed`. Click a call to open it in a new tab, and use `Load More Calls` at the bottom for older ones.',
      },
      { type: 'h3', text: 'My Calls (call agents)' },
      {
        type: 'p',
        text:
          'A call agent’s own calls are on **My Calls**. Only calls a reviewer has marked as assessed appear here. Filter by campaign, and click a call to open it in a new tab.',
      },
      {
        type: 'p',
        text:
          'A General Manager also gets a **Your team** panel. Choose an agent in `Viewing calls for` to see that agent’s assessed calls (for example John Doe’s), and `Back to my calls` to return to your own. `Manage my team` opens the page where you add and remove team members; see {{My team|#my-team}}.',
      },
    ],
  },

  {
    id: 'call-details',
    group: 'Review',
    title: 'Reviewing a call',
    lede: 'The heart of the tool: one call, its transcript, its assessment, and your judgement.',
    roles: ['sAdm', 'adm', 'qa', 'callAgent'],
    blocks: [
      {
        type: 'p',
        text:
          'The call page opens from Campaign Home, Search, Assigned to me, Disputes or My Calls. It is one long page. At the top are the agent’s name, the campaign and scorecard, the **Metadata** and **Summary** panels, a sentiment chart, and score tiles. Below that are three sections, **Overview**, **Transcript** and **QA**. Each one can be folded away, and once you scroll down a bar appears to jump between them.',
      },
      { type: 'h3', text: 'Score tiles' },
      {
        type: 'ul',
        items: [
          '**QA Score** (or **Quality Score**), **Compliance Score**, **Compliance** pass or fail, **Agent Conduct** (only when the scorecard asks conduct questions), **Critical Fail** (only when one was hit) and **Duration**.',
          'A **compliance-only scorecard** has no QA score. Its call page shows no QA Score or Compliance Score tile, the assessment is headed **Compliance Assessment**, and lists and exports show the QA score as a dash or *N/A*.',
        ],
      },
      { type: 'h3', text: 'Overview' },
      {
        type: 'p',
        text:
          'The agent, campaign, queue, language, topic, product and whether the call was resolved, followed by the AI-generated summary and sentiment. Read this first to orient yourself before listening.',
      },
      { type: 'h3', text: 'Transcript' },
      {
        type: 'ul',
        items: [
          'Each turn shows the **speaker name and timestamp**, with a sentiment icon beside it. Tags mark IVR, interruptions and detected categories.',
          '**Click any timestamp** to jump the audio to that moment.',
          'Where a translation exists, `Show English` / `Show Zulu` switches the whole transcript; the small `EN` / `ZU` button on a turn switches just that turn.',
          '`Swap Agent / Customer` fixes a call whose recording labelled the agent and customer the wrong way round. Reviewers get it on a plain two-speaker call that is not yet assessed. The swap re-scores the call, and any overrides on it are replaced.',
        ],
      },
      { type: 'h3', text: 'QA' },
      {
        type: 'p',
        text:
          'Under **Assessment Details**, each question shows its verdict, the reason behind it and the evidence quoted from the transcript. Click a quote’s time to jump to that line in the transcript. When a reviewer chose the evidence, it is labelled **Evidence chosen by the reviewer**.',
      },
      { type: 'h3', text: 'Overriding a verdict' },
      {
        type: 'steps',
        items: [
          {
            title: 'Click `Override`.',
            text:
              'Each question becomes editable: its answer, pass or fail, or score, and a **Reason** box pre-filled with the AI’s reasoning. Replace the reason with why you scored it your way.',
          },
          {
            title: 'Fix the evidence if needed.',
            text:
              'Untick `Keep` on a quote that no longer applies, or use `Cite a line` to quote the transcript yourself (up to 5 quotes per question). Where a question has pre-check steps, you can correct a step the AI got wrong.',
          },
          {
            title: 'Click `Save Changes`.',
            text:
              'The bar at the bottom counts the questions you have changed, and each changed question is marked `Changed`. If part of a save cannot be applied, a **Some changes were not saved** message says why. Saves are recorded in {{Audit|#audit}}.',
          },
        ],
      },
      { type: 'h3', text: 'Marking the call assessed' },
      {
        type: 'p',
        text:
          '`Mark as Assessed` locks the call: no further overrides or refreshes. It is also what lets a call agent see the call and dispute it. `Unmark as Assessed` is Super Admin only. A reviewer resolving a dispute reopens a call with `Change Assessment` instead; see {{Disputes|#disputes}}.',
      },
      {
        type: 'note',
        variant: 'caution',
        tag: 'Refresh Call',
        text:
          '`Refresh Call` re-scores the call with its current scorecard. Use the arrow beside it to pick a different scorecard from the call’s campaign. The new AI verdicts replace any overrides, and there is no confirmation step. Once a call is marked assessed, it can no longer be refreshed.',
      },
      { type: 'h3', text: 'Other controls on this page' },
      {
        type: 'ul',
        items: [
          '**Generative AI Query**: the panel on the right. Ask questions about this call in plain language; answers come from its transcript.',
          '`Mark as Non-sales`: flags an unsuccessful call so it can be left out of scores, with an optional reason. It locks nothing and works before or after assessment. `Mark as Sales` undoes it.',
          '**Edit** on the Metadata panel changes the agent, call date and time, conversation ID and DNIS. Super Admin only, and recorded in Audit.',
        ],
      },
      { type: 'h3', text: 'Good practice when assessing' },
      {
        type: 'ul',
        items: [
          'Listen to the complete recording before assessing.',
          'Evaluate each criterion objectively against the rubric, not against your impression of the call.',
          'Give specific feedback and examples where applicable.',
          'Consider both agent performance and customer satisfaction.',
        ],
      },
      { type: 'h3', text: 'What a call agent sees on this page' },
      {
        type: 'p',
        text:
          'A call agent opens their assessed calls from My Calls and sees the same summary, transcript and assessment, with the reasons and evidence behind each verdict. The differences:',
      },
      {
        type: 'ul',
        items: [
          'There is no Override, Mark as Assessed, Non-sales or Swap control.',
          '`Dispute` asks for a reason and sends the call for review. The page then shows `Disputed — pending review` and, later, **Dispute Resolved** with the outcome and the reviewer’s reason. See {{Disputes|#disputes}}.',
        ],
      },
    ],
  },

  {
    id: 'search',
    group: 'Review',
    title: 'Search',
    lede: 'Three depths of search, results a page at a time, and why some filters behave differently from others.',
    roles: ['sAdm', 'adm'],
    blocks: [
      {
        type: 'p',
        text:
          'Find it under **Quality › Search**. Typing in the top-bar search box and pressing Enter also opens this page, but what you typed is not applied; set the filters here.',
      },
      { type: 'h3', text: 'Pick your depth' },
      {
        type: 'ul',
        items: [
          '**Quick Filters**: one-click searches such as *Failed Compliance Today*, *Failed Conduct Today*, *High Scores (90+)*, *Assessed Calls*, *Unassigned Calls*, *Long Calls (>10 min)*, *Processing Calls* and *Uploaded This Week*.',
          '**Basic Search**: agent, conversation ID, campaign, template (pick a campaign first), date range, DNIS, QA score range, compliance, agent conduct and compliance priority.',
          '**Advanced**: job ID, assigned to, GUID, assessment status, status and duration. Anything you set on Basic Search still applies.',
        ],
      },
      { type: 'h3', text: 'The date range' },
      {
        type: 'ul',
        items: [
          'The date range limits every other filter: only calls inside it are searched.',
          'With no date range, a search covers the **last 5 days**. Searching by GUID, conversation ID, DNIS or job looks through all calls instead.',
        ],
      },
      { type: 'h3', text: 'How filters combine' },
      {
        type: 'p',
        text:
          'Picking several agents matches **any** of them. Every other filter **narrows** the result further.',
      },
      { type: 'h3', text: 'Results' },
      {
        type: 'ul',
        items: [
          'Results arrive **25 at a time, newest first**. `Load More Calls` at the bottom fetches the next 25.',
          'While more calls match, the count, the table’s own filters and `Export CSV` / `Export JSON` cover only the calls loaded so far. Load more first if you need them all.',
          'Clicking a result opens that call in a new tab.',
        ],
      },
      { type: 'h3', text: 'Fast filters vs. refining filters' },
      {
        type: 'p',
        text:
          'Some filters run against the indexed data and are fast; others refine the results after they have been pulled. When a search feels slow, lead with an indexed filter, then refine. The page’s **How search filters behave** panel has the same list.',
      },
      {
        type: 'table',
        caption: 'Filter behaviour',
        head: ['Indexed — fast', 'Applied afterwards — refines results'],
        rows: [
          ['Agent, Campaign, Template', 'Exact QA score range (refines the indexed range)'],
          ['Assigned To, Assessment Status, Compliance', 'Duration range, Status'],
          ['Date range, QA score range', 'GUID, Job ID'],
        ],
      },
    ],
  },
];

export default reviewing;
